import {
  AIExplanationInputTooLargeError,
  CommitInfo,
  explainSourceDiff,
  getSourceContent,
  handleLargeResponse
} from '_logics/LFCSourceUtil';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import {
  alpha,
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import LFCIconButton from '_components/inputs/LFCIconButton';
import {getLanguageByFileName} from '_logics/code-highlight';
import {selectGitVolatilePatterns, splitDeviceFilePath} from '_logics/pghistoryProfileMatch';
import {compileVolatileRules} from '_logics/pghistoryVolatileRules';
import {usePgHistoryProfiles} from '_logics/usePgHistoryProfiles';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import DiffDisplay from './DiffDisplay';
import AiMarkdownViewer from '_components/common/AiMarkdownViewer';
import {useMySettings} from '_contexts/MySettingsProvider';
import CommitHistoryCalendarPicker from './CommitHistoryCalendarPicker';

type FileHistoryDialogProps = {
  open: boolean;
  onClose: () => void;
  filePath: string;
  fileHistory: CommitInfo[];
  historyLoading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void | Promise<void>;
  loadMoreLoading?: boolean;
};

/**
 * ファイルパスから devices/ プレフィックスを除去して表示用パスを取得
 */
const getDisplayPath = (filePath: string): string => {
  // "devices/device-001/path/to/file.txt" -> "device-001/path/to/file.txt"
  return filePath.replace(/^devices\//, '');
};

/**
 * ファイル変更履歴ダイアログ
 */
const FileHistoryDialog = ({
  open,
  onClose,
  filePath,
  fileHistory,
  historyLoading,
  hasMore,
  onLoadMore,
  loadMoreLoading
}: FileHistoryDialogProps) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const mySettings = useMySettings();

  const [beforeIndex, setBeforeIndex] = useState<number | null>(null);
  const [afterIndex, setAfterIndex] = useState<number | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [beforeContent, setBeforeContent] = useState<string>('');
  const [afterContent, setAfterContent] = useState<string>('');
  const [changeType, setChangeType] = useState<'A' | 'M' | 'D' | undefined>(undefined);
  const [beforeExists, setBeforeExists] = useState<boolean>(true);
  const [afterExists, setAfterExists] = useState<boolean>(true);
  const [includeVolatileState, setIncludeVolatileState] = useState(false);

  const language = useMemo(() => getLanguageByFileName(filePath), [filePath]);
  const profiles = usePgHistoryProfiles();
  const volatileRules = useMemo(() => {
    const target = splitDeviceFilePath(filePath);
    if (!target) return [];
    return (
      compileVolatileRules(selectGitVolatilePatterns(profiles, target.device, target.path)) ?? []
    );
  }, [profiles, filePath]);
  const showVolatileToggle = Boolean(language?.supportsVolatileState) || volatileRules.length > 0;

  // 表示モードとズーム
  const [viewMode, setViewMode] = useState<'unified' | 'sideBySide'>('sideBySide');
  const [zoomPercent, setZoomPercent] = useState(100);
  const fontSize = (zoomPercent / 100) * 14;
  const handleZoomIn = () => setZoomPercent(prev => Math.min(prev + 5, 120));
  const handleZoomOut = () => setZoomPercent(prev => Math.max(prev - 5, 80));
  const handleResetZoom = () => setZoomPercent(100);

  // AI解説
  const [aiExplanation, setAiExplanation] = useState<string | null>(null);
  const [aiError, setAiError] = useState<'input-too-large' | 'generic' | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const aiAbortRef = useRef<AbortController | null>(null);
  const [aiWidth, setAiWidth] = useState(400);
  const aiResizing = useRef(false);

  const handleAiResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    aiResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!aiResizing.current) return;
      const dialogEl = document.getElementById('file-history-dialog-content');
      if (!dialogEl) return;
      const dialogRect = dialogEl.getBoundingClientRect();
      const newWidth = dialogRect.right - e.clientX;
      setAiWidth(Math.max(200, Math.min(newWidth, dialogRect.width * 0.6)));
    };
    const onMouseUp = () => {
      aiResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const handleAiExplain = useCallback(async () => {
    if (aiOpen && (aiExplanation || aiError)) {
      setAiOpen(false);
      return;
    }

    setAiOpen(true);
    setAiLoading(true);
    setAiExplanation(null);
    setAiError(null);

    aiAbortRef.current?.abort();
    const abortController = new AbortController();
    aiAbortRef.current = abortController;

    const fileName = filePath.split('/').pop() || '';
    try {
      const result = await explainSourceDiff(
        filePath,
        fileName,
        beforeContent,
        afterContent,
        abortController.signal,
        mySettings.locale as 'ja' | 'en' | undefined
      );
      setAiExplanation(result.explanation);
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'CanceledError') return;
      setAiError(error instanceof AIExplanationInputTooLargeError ? 'input-too-large' : 'generic');
    } finally {
      setAiLoading(false);
    }
  }, [filePath, beforeContent, afterContent, aiOpen, aiExplanation, aiError, mySettings.locale]);

  // fileHistoryはAPIから新しい順（最新が先）で返されるので、そのまま使用
  const sortedHistory = fileHistory;

  // ダイアログが閉じたら選択状態とAI解説をリセット
  useEffect(() => {
    if (!open) {
      setAfterIndex(null);
      setBeforeIndex(null);
      setAiExplanation(null);
      setAiError(null);
      setAiOpen(false);
      setAiLoading(false);
      aiAbortRef.current?.abort();
    }
  }, [open]);

  // ファイルを切り替えたときは、実行時I/O状態の表示設定を引き継がない。
  useEffect(() => {
    setIncludeVolatileState(false);
  }, [filePath, open]);

  // ダイアログが開いたらデフォルト選択を設定（既に選択がある場合はキープ）
  useEffect(() => {
    if (open && !historyLoading && sortedHistory.length > 0) {
      // 既に選択がある場合はキープ（更に表示でリセットしない）
      if (afterIndex !== null) {
        return;
      }
      // デフォルト: After=最新(0), Before=1つ前(1) or null
      const newAfterIndex = 0;
      const newBeforeIndex = sortedHistory.length > 1 ? 1 : null;
      setAfterIndex(newAfterIndex);
      setBeforeIndex(newBeforeIndex);
    } else if (open && !historyLoading && sortedHistory.length === 0) {
      setAfterIndex(null);
      setBeforeIndex(null);
      setBeforeContent('');
      setAfterContent('');
      setChangeType(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, historyLoading, sortedHistory.length]);

  /**
   * ファイル内容を取得（404の場合は空文字列を返す）
   */
  const fetchContentSafe = async (
    path: string,
    commitId: string
  ): Promise<{content: string; notFound: boolean; isLarge: boolean}> => {
    try {
      const result = await getSourceContent(path, commitId);
      if (result.type === 'large-response') {
        return {content: '', notFound: false, isLarge: true};
      }
      return {content: result.content || '', notFound: false, isLarge: false};
    } catch (error: any) {
      // 404エラー（ファイルが存在しない = 削除されたか、まだ作成されていない）
      if (error?.response?.status === 404) {
        return {content: '', notFound: true, isLarge: false};
      }
      throw error;
    }
  };

  // 選択中のコミットID（依存配列用）
  const afterCommitId = afterIndex !== null ? sortedHistory[afterIndex]?.commitId : null;
  const beforeCommitId = beforeIndex !== null ? sortedHistory[beforeIndex]?.commitId : null;

  // コミット選択が変わったらAI解説をリセット
  useEffect(() => {
    setAiExplanation(null);
    setAiError(null);
    setAiOpen(false);
    aiAbortRef.current?.abort();
  }, [beforeCommitId, afterCommitId]);

  // コミットID変更時に差分を取得
  useEffect(() => {
    if (!open || !filePath || !afterCommitId) {
      return;
    }

    const fetchDiff = async () => {
      setDiffLoading(true);

      try {
        const [afterResult, beforeResult] = await Promise.all([
          fetchContentSafe(filePath, afterCommitId),
          beforeCommitId
            ? fetchContentSafe(filePath, beforeCommitId)
            : Promise.resolve({content: '', notFound: true, isLarge: false})
        ]);

        // ファイル存在フラグを更新
        setBeforeExists(!beforeResult.notFound && !beforeResult.isLarge);
        setAfterExists(!afterResult.notFound && !afterResult.isLarge);

        if (afterResult.isLarge || beforeResult.isLarge) {
          setAfterContent(t('(大容量ファイルのため差分表示できません)'));
          setBeforeContent('');
          setChangeType(undefined);
        } else {
          if (beforeResult.notFound && !afterResult.notFound) {
            setChangeType('A');
          } else if (!beforeResult.notFound && afterResult.notFound) {
            setChangeType('D');
          } else {
            setChangeType('M');
          }
          setAfterContent(afterResult.content);
          setBeforeContent(beforeResult.content);
        }
      } catch (error) {
        console.error('差分取得エラー:', error);
        setAfterContent(t('(差分の取得に失敗しました)'));
        setBeforeContent('');
      } finally {
        setDiffLoading(false);
      }
    };

    fetchDiff();
  }, [open, beforeCommitId, afterCommitId, filePath, t]);

  const handleSelectBefore = useCallback(
    (index: number) => {
      if (afterIndex !== null && index <= afterIndex) return;
      setBeforeIndex(index);
    },
    [afterIndex]
  );

  const handleSelectAfter = useCallback(
    (index: number) => {
      if (beforeIndex !== null && index >= beforeIndex) return;
      setAfterIndex(index);
    },
    [beforeIndex]
  );

  const isBeforeCommitSelectable = useCallback(
    (index: number) => afterIndex === null || index > afterIndex,
    [afterIndex]
  );

  const isAfterCommitSelectable = useCallback(
    (index: number) => beforeIndex === null || index < beforeIndex,
    [beforeIndex]
  );

  const calendarLocale = mySettings.locale === 'ja' ? 'ja' : 'en';

  const beforeHeaderControl = useMemo(
    () => (
      <Box sx={{width: 180, flexShrink: 0}}>
        <CommitHistoryCalendarPicker
          label={t('Before変更日時')}
          commits={sortedHistory}
          selectedIndex={beforeIndex}
          onSelect={handleSelectBefore}
          isCommitSelectable={isBeforeCommitSelectable}
          locale={calendarLocale}
          olderMonthLabel={t('前の変更月')}
          newerMonthLabel={t('次の変更月')}
          loadingText={t('変更履歴を読み込み中')}
          disabled={historyLoading || sortedHistory.length === 0}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          loadMoreLoading={loadMoreLoading}
        />
      </Box>
    ),
    [
      t,
      sortedHistory,
      beforeIndex,
      handleSelectBefore,
      isBeforeCommitSelectable,
      calendarLocale,
      historyLoading,
      hasMore,
      onLoadMore,
      loadMoreLoading
    ]
  );

  const afterHeaderControl = useMemo(
    () => (
      <Box sx={{width: 180, flexShrink: 0}}>
        <CommitHistoryCalendarPicker
          label={t('After変更日時')}
          commits={sortedHistory}
          selectedIndex={afterIndex}
          onSelect={handleSelectAfter}
          isCommitSelectable={isAfterCommitSelectable}
          locale={calendarLocale}
          olderMonthLabel={t('前の変更月')}
          newerMonthLabel={t('次の変更月')}
          loadingText={t('変更履歴を読み込み中')}
          disabled={historyLoading || sortedHistory.length === 0}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          loadMoreLoading={loadMoreLoading}
        />
      </Box>
    ),
    [
      t,
      sortedHistory,
      afterIndex,
      handleSelectAfter,
      isAfterCommitSelectable,
      calendarLocale,
      historyLoading,
      hasMore,
      onLoadMore,
      loadMoreLoading
    ]
  );

  /**
   * 日時文字列をファイル名用にフォーマットする（YYYYMMDD_HHmmss）
   */
  const formatDateForFileName = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const hh = String(date.getHours()).padStart(2, '0');
      const mi = String(date.getMinutes()).padStart(2, '0');
      const ss = String(date.getSeconds()).padStart(2, '0');
      return `${yyyy}${mm}${dd}_${hh}${mi}${ss}`;
    } catch {
      return 'unknown';
    }
  };

  /**
   * ファイルをダウンロードする
   */
  const handleDownload = async (commitId: string, commitDate: string) => {
    try {
      const result = await getSourceContent(filePath, commitId);

      let content: string;
      if (result.type === 'large-response' && result.url) {
        // large-responseの場合はURLからコンテンツを取得
        const largeContent = await handleLargeResponse<{content: string}>(result);
        content = largeContent.content;
      } else {
        content = result.content || '';
      }

      // ファイル名を生成（ベース名_日時.拡張子）
      const originalFileName = filePath.split('/').pop() || 'file';
      const lastDotIndex = originalFileName.lastIndexOf('.');
      const baseName =
        lastDotIndex > 0 ? originalFileName.substring(0, lastDotIndex) : originalFileName;
      const ext = lastDotIndex > 0 ? originalFileName.substring(lastDotIndex) : '';
      const dateStr = formatDateForFileName(commitDate);
      const downloadFileName = `${baseName}_${dateStr}${ext}`;

      // Blobを作成してダウンロード
      const blob = new Blob([content], {type: 'text/plain;charset=utf-8'});
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('ダウンロードエラー:', error);
    }
  };

  /**
   * Beforeファイルのダウンロード
   */
  const beforeCommit = beforeIndex !== null ? sortedHistory[beforeIndex] : null;
  const handleDownloadBefore =
    beforeExists && beforeCommit
      ? () => handleDownload(beforeCommit.commitId, beforeCommit.date)
      : null;

  /**
   * Afterファイルのダウンロード
   */
  const afterCommit = afterIndex !== null ? sortedHistory[afterIndex] : null;
  const handleDownloadAfter =
    afterExists && afterCommit
      ? () => handleDownload(afterCommit.commitId, afterCommit.date)
      : null;

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle
        sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5}}
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1}}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={getDisplayPath(filePath)}
          >
            {getDisplayPath(filePath)}
          </Typography>
          {language ? (
            <Typography
              variant="caption"
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                flexShrink: 0
              }}
            >
              {language.badge}
            </Typography>
          ) : null}
          {showVolatileToggle && (
            <Tooltip title={t('実行時の値も差分に含む')} placement="bottom">
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{flexShrink: 0}}>
                <Switch
                  size="small"
                  checked={includeVolatileState}
                  onChange={(_, checked) => setIncludeVolatileState(checked)}
                  inputProps={{'aria-label': t('実行時の値')}}
                />
                <Typography variant="body2" sx={{whiteSpace: 'nowrap'}}>
                  {t('実行時の値')}
                </Typography>
              </Stack>
            </Tooltip>
          )}
        </Box>
        {/* ツールバー */}
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          <Tooltip title={t('縮小')}>
            <span>
              <LFCIconButton
                color="primary"
                size="small"
                onClick={handleZoomOut}
                disabled={zoomPercent <= 80}
              >
                <RemoveIcon fontSize="small" />
              </LFCIconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('リセット')}>
            <Typography
              variant="body2"
              sx={{minWidth: 40, textAlign: 'center', fontWeight: 'bold', cursor: 'pointer'}}
              onClick={handleResetZoom}
            >
              {zoomPercent}%
            </Typography>
          </Tooltip>
          <Tooltip title={t('拡大')}>
            <span>
              <LFCIconButton
                color="primary"
                size="small"
                onClick={handleZoomIn}
                disabled={zoomPercent >= 120}
              >
                <AddIcon fontSize="small" />
              </LFCIconButton>
            </span>
          </Tooltip>
          <Box sx={{width: 8}} />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
            color="primary"
          >
            <ToggleButton value="unified">
              <Tooltip title={t('行単位表示')}>
                <ViewStreamIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="sideBySide">
              <Tooltip title={t('横並び表示')}>
                <ViewColumnIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{width: 24}} />
          <LFCIconButton onClick={() => onClose()} size="small">
            <CloseIcon />
          </LFCIconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent
        id="file-history-dialog-content"
        sx={{p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'row'}}
      >
        {/* DiffDisplay */}
        <Box sx={{flex: 1, overflow: 'hidden'}}>
          <DiffDisplay
            beforeContent={beforeContent}
            afterContent={afterContent}
            changeType={changeType}
            maxHeight="100%"
            fileName={getDisplayPath(filePath)}
            onDownloadBefore={handleDownloadBefore}
            onDownloadAfter={handleDownloadAfter}
            hideToolbar={true}
            viewMode={viewMode}
            fontSize={fontSize}
            beforeHeaderControl={beforeHeaderControl}
            afterHeaderControl={afterHeaderControl}
            includeVolatileState={includeVolatileState}
            volatileRules={volatileRules}
            loading={diffLoading || afterIndex === null}
            onAiExplain={handleAiExplain}
            aiOpen={aiOpen}
            aiLoading={aiLoading}
          />
        </Box>

        {/* AI解説パネル（右側固定） */}
        {aiOpen && (
          <>
            <Box
              onMouseDown={handleAiResizeStart}
              sx={{
                width: 6,
                cursor: 'col-resize',
                bgcolor: isDark ? 'grey.700' : 'grey.300',
                '&:hover': {bgcolor: 'primary.main', opacity: 0.5},
                transition: 'background-color 0.15s',
                flexShrink: 0
              }}
            />
            <Box
              sx={{
                width: aiWidth,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                borderLeft: 1,
                borderColor: 'divider',
                bgcolor: isDark ? alpha('#1a237e', 0.15) : alpha('#e3f2fd', 0.5)
              }}
            >
              {aiError ? (
                <Alert severity={aiError === 'input-too-large' ? 'warning' : 'error'} sx={{m: 2}}>
                  {aiError === 'input-too-large'
                    ? t(
                        'AI解説の対象ファイルが大きすぎます。範囲を絞るか、ファイルを分割してください。'
                      )
                    : t('AI解説の取得に失敗しました。')}
                </Alert>
              ) : (
                <AiMarkdownViewer content={aiExplanation || ''} loading={aiLoading} />
              )}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FileHistoryDialog;
